import type { KeyboardEvent } from "react";

/**
 * WAI-ARIA tabs keyboard pattern helper.
 * - Space (and Enter, native on buttons) activates the focused tab
 * - ArrowLeft / ArrowRight move focus and activation between tabs
 * - Home / End jump to first / last tab
 *
 * Pair with `tabIndex={isActive ? 0 : -1}` on each tab and ensure each tab's
 * DOM `id` is `${tabIdPrefix}${tabId}` so focus can be moved programmatically.
 */
export function useTablistKeyboard<T extends string>(
  tabIds: readonly T[],
  setActive: (id: T) => void,
  tabIdPrefix = "tab-",
) {
  return (currentIndex: number) => (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === " " || e.key === "Spacebar") {
      e.preventDefault();
      setActive(tabIds[currentIndex]);
      return;
    }
    let nextIndex: number | null = null;
    if (e.key === "ArrowRight") nextIndex = (currentIndex + 1) % tabIds.length;
    else if (e.key === "ArrowLeft")
      nextIndex = (currentIndex - 1 + tabIds.length) % tabIds.length;
    else if (e.key === "Home") nextIndex = 0;
    else if (e.key === "End") nextIndex = tabIds.length - 1;
    if (nextIndex !== null) {
      e.preventDefault();
      const next = tabIds[nextIndex];
      setActive(next);
      // Move focus to match the new active tab (roving tabindex)
      requestAnimationFrame(() => {
        document.getElementById(`${tabIdPrefix}${next}`)?.focus();
      });
    }
  };
}
